import axios from 'axios';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus } from "react-icons/fa";
import StatementModalInput from './StatementModalInput';
import { useNavigate } from 'react-router-dom';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "38px",
        height: "38px",
        fontSize: "0.875rem",
        borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(37,99,235,0.15)" : "none",
        borderRadius: "10px",
        transition: "all 0.2s ease",
        "&:hover": { borderColor: "#2563eb" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: "38px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 8px" }),
    clearIndicator: (base) => ({ ...base, padding: "0 8px" }),
    menu: (base) => ({ ...base, zIndex: 50, fontSize: "0.875rem", borderRadius: "10px", overflow: "hidden" }),
    option: (base, { isFocused, isSelected }) => ({
        ...base,
        backgroundColor: isSelected ? "#2563eb" : isFocused ? "#eff6ff" : "white",
        color: isSelected ? "white" : "#111827",
        cursor: "pointer",
    }),
};

const editableClass =
    "w-full px-3 py-2 text-sm border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all";

const readonlyClass =
    "w-full px-3 py-2 text-sm border border-gray-200 rounded-[10px] bg-gray-50 text-gray-500 cursor-not-allowed";

const getEmptyItem = () => ({
    GroupName: '',
    ItemName: '',
    BrandName: '',
    vendor_name: '',
    UOM: '',
    CurrentLevel: 0,
    Rate: '',
    Total: 0,
    Type: 'Food',
    itemUnit: '',
    itemUom: '',
    taxable: false,
    stocktype: '',
    exp_date: '',
    hasExpiry: false,
});

const StatementModal = ({
    showModal,
    handleModalClose,
    token,
    baseUrl,
    selectedOutlet,
    loading,
    setLoading,
    fetchStockData,
}) => {
    const uomOptions = ['kg', 'gm', 'ltr', 'ml', 'pcs', 'box', 'packet', 'unit', 'bottle', 'case', 'bunch'];
    const unitUomOptions = ['kg', 'gm', 'ltr', 'ml', 'pcs', 'packet', 'unit', 'bottle'];
    const typeOptions = ['Food', 'Beverage', 'Others'];
    const stockTypeOptions = ["Stockable", "Non-Stockable"];

    const [formData, setFormData] = useState([getEmptyItem()]);
    const [itemCategory, setItemCategory] = useState("raw_material");

    const uomSelectOptions = uomOptions.map(opt => ({ label: opt, value: opt }));
    const unitUomSelectOptions = unitUomOptions.map(opt => ({ label: opt, value: opt }));

    const navigate = useNavigate();

    const handleChange = (index, e) => {
        const { name, value, type, checked } = e.target;
        const updated = [...formData];

        if (type === "checkbox") {
            updated[index][name] = checked;
        } else {
            updated[index][name] = value;
        }

        if (name === "hasExpiry") {
            updated[index].exp_date = checked
                ? new Date().toLocaleDateString('en-CA')
                : "";
        }

        const qty = parseFloat(updated[index].CurrentLevel) || 0;
        const rate = parseFloat(updated[index].Rate) || 0;
        updated[index].Total = qty * rate;

        setFormData(updated);
    };

    const handleSelectChange = (index, field, selected) => {
        const updated = [...formData];
        updated[index][field] = selected ? selected.value : "";
        setFormData(updated);
    };

    const addNewRow = () => {
        setFormData(prev => [
            ...prev,
            {
                ...getEmptyItem(),
                GroupName: itemCategory === "production" ? "Finished Goods" : "",
            },
        ]);
    };

    const removeRow = (index) => {
        if (formData.length === 1) return;
        setFormData(prev => prev.filter((_, i) => i !== index));
    };

    const handleCategoryChange = (category) => {
        setItemCategory(category);
        setFormData(prev =>
            prev.map(item => ({
                ...item,
                GroupName: category === "production" ? "Finished Goods" : "",
            }))
        );
    };

    useEffect(() => {
        if (showModal) setItemCategory("raw_material");
    }, [showModal]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const invalidStockType = formData.some(item => !item.stocktype);
        if (invalidStockType) {
            toast.error("Please select stock type for all items");
            return;
        }

        const invalidSecondaryUnit = formData.some(item => !item.itemUom);
        if (invalidSecondaryUnit) {
            toast.error("Please select a secondary unit for all items");
            return;
        }

        const invalidUnitDef = formData.some(
            item => !item.itemUnit || parseFloat(item.itemUnit) <= 0
        );
        if (invalidUnitDef) {
            toast.error("Please enter a valid unit definition quantity for all items");
            return;
        }

        setLoading(true);

        try {
            await axios.post(`${baseUrl}/checkAndInsertStockItems`, {
                items: formData.map(item => ({
                    ItemName: item.ItemName,
                    OutletName: selectedOutlet || "",
                    GroupName: item.GroupName,
                    BrandName: item.BrandName || "",
                    UOM: item.UOM || "",
                    CurrentLevel: item.CurrentLevel || 0,
                    Rate: item.Rate || 0,
                    Total: item.Total || 0,
                    Type: item.Type || "",
                    vendor_name: item.vendor_name || "",
                    StockType: item.stocktype || "",
                    exp_date: item.exp_date || null,
                    taxable: item.taxable ? 1 : 0,
                    primary_unit: item.UOM,
                    secondary_unit: item.itemUom,
                })),
            });

            await axios.post(`${baseUrl}/unit-definitions`, {
                token,
                outlet: selectedOutlet,
                items: formData.map(item => ({
                    name: item.ItemName,
                    unit: parseFloat(item.itemUnit),
                    uom: item.itemUom,
                    rate: parseFloat(item.Rate || 0),
                })),
            });

            toast.success("Stock items successfully created");
            fetchStockData(token, selectedOutlet);

            if (itemCategory === "production") {
                handleAddConversion(formData.map(item => item.ItemName));
            }

            handleModalClose();
            setFormData([getEmptyItem()]);

        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to create stock items"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleAddConversion = (itemNames = []) => {
        toast.info(
            <div className="p-2">
                <p className="mb-2 text-white">
                    {itemNames.length > 1
                        ? "Do you want to link these items to recipes?"
                        : "Do you want to link this item to a recipe?"}
                </p>

                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={() => {
                            navigate('/central-kitchen/conversion-factor', {
                                state: {
                                    itemNames,
                                }
                            })
                        }}
                    >
                        Link
                    </button>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div >,
            {
                autoClose: false,
                closeButton: false,
            }
        );
    }

    return (
        <Modal show={showModal} onHide={handleModalClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-5 fw-semibold">
                    Add New Stock Items
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <form onSubmit={handleSubmit} className="text-sm">
                    <StatementModalInput
                        itemCategory={itemCategory}
                        handleCategoryChange={handleCategoryChange}
                        formData={formData}
                        handleChange={handleChange}
                        handleSelectChange={handleSelectChange}
                        removeRow={removeRow}
                        typeOptions={typeOptions}
                        stockTypeOptions={stockTypeOptions}
                        uomSelectOptions={uomSelectOptions}
                        unitUomSelectOptions={unitUomSelectOptions}
                        readonlyClass={readonlyClass}
                        editableClass={editableClass}
                        selectStyles={selectStyles}
                    />

                    <div className="mt-4 flex justify-between">
                        <button
                            type="button"
                            onClick={addNewRow}
                            className="px-4 py-2 flex gap-1.5 items-center rounded-md font-medium bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md text-sm transition"
                        >
                            <FaPlus size={12} />
                            Add Item
                        </button>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleModalClose}
                                className="px-4 py-2 rounded-md font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 hover:shadow-md text-sm transition"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Saving..." : "Save Items"}
                            </button>
                        </div>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default StatementModal;